/**
 * Состояния загрузки, пустоты и ошибки.
 *
 * Каждый ключевой экран обязан иметь все три: скелет вместо прыгающей вёрстки,
 * понятное пустое состояние вместо белого пятна и ошибку с возможностью
 * повторить (документ «Структура интерфейса», §20).
 */

import { AlertTriangleIcon, RefreshCwIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/**
 * Пустое состояние.
 *
 * Пустой экран должен объяснять три вещи: чего здесь нет, зачем оно нужно и
 * что сделать прямо сейчас. Одного заголовка «Пусто» мало — человек, впервые
 * открывший раздел, не знает, что в него класть.
 *
 * `hint` — необязательная подсказка мелким шрифтом: пример содержимого или
 * связанный раздел, куда стоит заглянуть вместо этого.
 */
export function EmptyState({
  icon,
  title,
  description,
  hint,
  action,
  secondaryAction,
  className,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  hint?: ReactNode;
  action?: ReactNode;
  secondaryAction?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'fade-in flex flex-col items-center justify-center px-6 py-12 text-center',
        className,
      )}
    >
      {icon ? (
        <div className="mb-3 rounded-xl bg-muted/60 p-3 text-muted-foreground [&>svg]:size-7">
          {icon}
        </div>
      ) : null}
      <p className="text-sm font-medium text-foreground">{title}</p>
      {description ? (
        <p className="mt-1.5 max-w-md text-xs leading-relaxed text-muted-foreground">
          {description}
        </p>
      ) : null}
      {action || secondaryAction ? (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          {action}
          {secondaryAction}
        </div>
      ) : null}
      {hint ? (
        <p className="mt-3 max-w-md text-2xs leading-relaxed text-muted-foreground/80">{hint}</p>
      ) : null}
    </div>
  );
}

export function ErrorState({
  error,
  onRetry,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}) {
  const message =
    error instanceof ApiError
      ? error.message
      : error instanceof Error
        ? error.message
        : 'Не удалось загрузить данные.';
  const hint = error instanceof ApiError ? error.hint : undefined;
  const requestId = error instanceof ApiError ? error.requestId : undefined;

  return (
    <div className={cn('flex flex-col items-center px-6 py-12 text-center', className)}>
      <AlertTriangleIcon className="mb-3 size-7 text-tone-danger" />
      <p className="text-sm font-medium text-foreground">{message}</p>
      {hint ? (
        <p className="mt-1.5 max-w-md text-xs leading-relaxed text-muted-foreground">{hint}</p>
      ) : null}
      {onRetry ? (
        <Button variant="outline" size="sm" className="mt-4" onClick={onRetry}>
          <RefreshCwIcon />
          Повторить
        </Button>
      ) : null}
      {requestId ? (
        <p className="mt-4 font-mono text-2xs text-muted-foreground/70">
          Идентификатор запроса: {requestId}
        </p>
      ) : null}
    </div>
  );
}

/** Скелет списка карточек. */
export function CardListSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="surface-card space-y-3 border border-border/50 p-4">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-4/5" />
          <div className="flex gap-2 pt-1">
            <Skeleton className="h-5 w-16" />
            <Skeleton className="h-5 w-20" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Скелет строк таблицы. */
export function TableSkeleton({ rows = 5, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div className="divide-y divide-border">
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div key={rowIndex} className="flex items-center gap-4 px-3 py-2.5">
          {Array.from({ length: columns }, (_, columnIndex) => (
            <Skeleton
              key={columnIndex}
              className={cn('h-3.5', columnIndex === 0 ? 'w-1/3' : 'w-20')}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
